import { observer } from 'mobx-react-lite'
import { type FC } from 'react'
import { uiStore } from 'shared/store/uiStore'
import { type ActionItem, Actions, Divider, variantActionsProps } from 'shared/ui'
import { logger } from 'shared/lib'
import { ContactsApi } from 'entities/Contacts'
import { BroadcastsRoutes } from 'entities/ExportRouters'
import { EnrollInCampaignAction } from 'entities/Campaign/ui'
import { type IResponseFilterSegment } from 'entities/Segment'
import { type IContactsDataParams, type IFiltersListGroups } from 'entities/Contacts/api/types'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type ContactsTagsModalStore } from 'widgets/ContactsTagsModal'
import { PowerDialerEditorAction } from 'widgets/PowerDialer/ui/PowerDialerEditor'
import { ChatbotEnrollAction } from 'widgets/ChatbotEnroll'
import { WorkflowEnrollAction } from 'widgets/WorkflowEnroll'
import { ContactsOptInModalStore } from 'widgets/ContactsOptInModal'

type ContactStatisticBulkActionsProps = {
  search: string
  selectedContactIds: (string | number)[]
  onSuccessDelete: () => void
  contactsTagsModalStore: ContactsTagsModalStore
  withNewActions?: boolean
  additionActions?: ActionItem[]
  filtersListGroups?: IFiltersListGroups
  filtersList?: IResponseFilterSegment[]
  bulkAll?: boolean
}

type IParams = {
  contact_ids: number[]
  filters_list_groups?: string
}

export const ContactStatisticBulkActions: FC<ContactStatisticBulkActionsProps> = observer(
  ({
    search,
    selectedContactIds,
    onSuccessDelete,
    contactsTagsModalStore,
    withNewActions,
    additionActions = [],
    filtersListGroups,
    filtersList,
    bulkAll,
  }) => {
    const { openValidateModal, openDeleteModal, deleteContacts } =
      contactsTagsModalStore.selectedModalStore
    const selectedIds = selectedContactIds.map(Number)
    const entities = useEntitiesContext()
    const user = entities.usersStore.user
    const isViewOnlyRole = user?.isViewOnlyRole
    const permissionCanManageTextBots =
      entities.userPermissionsStore.getItem('can_manage_text_bots')
    const canManageTextBots = !Boolean(isViewOnlyRole || !permissionCanManageTextBots)
    const permissionCanManageCampaigns =
      entities.userPermissionsStore.getItem('can_manage_campaigns')
    const canManageCampaigns = !Boolean(isViewOnlyRole || !permissionCanManageCampaigns)

    const onManageTags = () => {
      contactsTagsModalStore.onOpen({
        ids: selectedIds,
        searchParams: search,
        isBulkAll: bulkAll,
        filtersList: filtersList,
      })
    }

    const onSendBroadcast = () => {
      const params: IParams = {
        contact_ids: selectedIds,
      }

      if (filtersListGroups) params.filters_list_groups = JSON.stringify(filtersListGroups)

      uiStore.changeRoute({
        path: `${BroadcastsRoutes.root}/${BroadcastsRoutes.create}`,
        options: {
          state: params,
        },
      })
    }

    const onValidate = () =>
      openValidateModal({
        selectedIds: selectedIds as number[],
        search,
        bulkAllMode: bulkAll,
        filtersList,
      })

    const onOptIn = async () => {
      const contacts = await entities.contactsStore.getByIds(selectedIds as number[])

      if (contacts) {
        new ContactsOptInModalStore(entities).handleOpen(contacts, {
          isBulkAll: !!bulkAll,
          search,
          filtersList,
        })
      }
    }

    const onDelete = async () => {
      const isBulkAll = bulkAll && filtersListGroups

      let selectedIdsLength: number

      if (isBulkAll) {
        selectedIdsLength = (await getContactsCount()) ?? 0
      } else {
        selectedIdsLength = selectedIds.length
      }

      void openDeleteModal({
        selectedIdsLength,
        primaryCallback: () =>
          deleteContacts({
            bulkAllMode: bulkAll,
            ids: !isBulkAll ? (selectedIds as number[]) : undefined,
            search,
            successCallback: onSuccessDelete,
            filtersList,
            count: selectedIdsLength,
          }),
      })
    }

    const getContactsCount = async () => {
      if (!filtersListGroups) return

      try {
        const { data } = await ContactsApi.getContactsFiltersCount({
          filtersListGroups,
        })

        return data.count
      } catch (e) {
        logger.error(e)
      }
    }

    const contactsData: IContactsDataParams = {
      search: search,
      selectedIds: !bulkAll ? selectedIds : [],
      bulkAll: bulkAll,
      contactsParams: {
        filtersList: filtersList,
      },
    }

    return (
      <Actions
        {...variantActionsProps.headerSmall}
        showItems={3}
        actions={[
          ...additionActions,
          {
            icon: 'check1',
            text: 'Validate',
            onAction: onValidate,
          },
          {
            icon: 'tag',
            text: 'Manage tags',
            onAction: onManageTags,
          },
          withNewActions &&
            canManageTextBots && {
              buttonComponent: (
                <ChatbotEnrollAction contactsData={contactsData} variant={'button'} />
              ),
              dropdownItemComponent: (onCloseMenu) => (
                <ChatbotEnrollAction
                  variant={'dropdownItem'}
                  onClick={onCloseMenu}
                  contactsData={contactsData}
                />
              ),
            },
          withNewActions &&
            canManageCampaigns && {
              buttonComponent: (
                <EnrollInCampaignAction variant={'button'} contactsData={contactsData} />
              ),
              dropdownItemComponent: (onCloseMenu) => (
                <EnrollInCampaignAction
                  variant={'dropdownItem'}
                  onClick={onCloseMenu}
                  contactsData={contactsData}
                />
              ),
            },
          withNewActions && {
            buttonComponent: (
              <PowerDialerEditorAction variant={'button'} contactsData={contactsData} />
            ),
            dropdownItemComponent: (onCloseMenu) => (
              <PowerDialerEditorAction
                variant={'dropdownItem'}
                onClick={onCloseMenu}
                contactsData={contactsData}
              />
            ),
          },
          withNewActions && {
            buttonComponent: (
              <WorkflowEnrollAction contactsData={contactsData} variant={'button'} />
            ),
            dropdownItemComponent: (onCloseMenu) => (
              <WorkflowEnrollAction
                variant={'dropdownItem'}
                onClick={onCloseMenu}
                contactsData={contactsData}
              />
            ),
          },
          {
            icon: 'broadcasts',
            text: 'Send broadcast',
            onAction: onSendBroadcast,
          },
          withNewActions && {
            dropdownItemComponent: () => <Divider sx={{ marginY: '5px', marginX: '-4px' }} />,
          },
          {
            icon: 'toggle',
            text: 'Opt-in status',
            onAction: onOptIn,
          },
          withNewActions && {
            dropdownItemComponent: () => <Divider sx={{ marginY: '5px', marginX: '-4px' }} />,
          },
          {
            icon: 'delete',
            text: 'Delete',
            onAction: onDelete,
          },
        ]}
      />
    )
  }
)
